/**
 * ShopifyStoresCard — manages connected Shopify stores on the Sources
 * page. Stores are 1:1 with flows: each shop can be bound to at most one
 * flow's trigger at a time. The "Used by …" line surfaces the active
 * binding so the merchant can see at a glance which flow owns each shop.
 */

import { Link } from "react-router-dom";
import { ExternalLink, Plus, Trash2, Info, RefreshCw } from "lucide-react";
import { useState } from "react";
import { getApiClient, type ShopifyStore } from "../../lib/apiClient";
import { useSourcesStore } from "./sourcesStore";
import { RealSyncProgress } from "./SyncProgress";
import { ConnectShopifyWizard } from "./ConnectShopifyWizard";

export function ShopifyStoresCard() {
  const stores = useSourcesStore((s) => s.shopifyStores);
  const refresh = useSourcesStore((s) => s.refresh);
  const [connecting, setConnecting] = useState(false);
  const isOffline = getApiClient().mode === "studio";

  return (
    <section className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
      <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Shopify stores</h2>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            One flow per store. Disconnecting orphans the bound flow and
            zeroes the access token.
          </p>
        </div>
        <button
          onClick={() => setConnecting(true)}
          disabled={isOffline}
          title={isOffline ? "Connect Shopify isn't available in offline Studio mode" : undefined}
          className="flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-2.5 py-1 text-xs font-medium hover:bg-category-trigger/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          Connect store
        </button>
      </header>
      <div>
        {stores.length === 0 ? (
          <p className="px-4 py-6 text-sm text-neutral-500 text-center">
            No Shopify stores connected yet. Click <strong>Connect store</strong>{" "}
            to start the OAuth handshake.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {stores.map((s) => (
              <StoreRow key={s.shop_domain} store={s} onMutated={refresh} />
            ))}
          </ul>
        )}
      </div>
      <ConnectShopifyWizard
        open={connecting}
        onClose={() => setConnecting(false)}
        returnTo="/sources"
      />
      {stores.length > 0 ? <ShopifyRedirectHelp /> : null}
    </section>
  );
}

/**
 * Expanding help block for merchants whose buyers are still landing on
 * Shopify's native checkout. Three theme-level settings are the usual
 * culprits: the TAE block isn't placed, Buy-Now is enabled on product
 * pages (bypasses the cart), or the cart drawer checkout button still
 * renders its own link. We can only hide buttons we can reach via DOM,
 * so the rest is theme configuration the merchant owns.
 */
function ShopifyRedirectHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-[12px] text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900/40"
      >
        <Info className="w-3.5 h-3.5 flex-none" />
        <span className="flex-1 text-left">
          Buyers still landing on Shopify's native checkout?
        </span>
        <span className="text-neutral-400">{open ? "−" : "+"}</span>
      </button>
      {open ? (
        <div className="px-4 pb-4 text-[12px] text-neutral-600 dark:text-neutral-300 leading-relaxed space-y-3">
          <p>
            Our Theme App Extension only hides Shopify's checkout button where
            we can reach it via the DOM — the <em>cart page</em>. Four things
            must line up:
          </p>
          <ol className="list-decimal ml-5 space-y-2">
            <li>
              <strong>The extension must be deployed from the Shopify CLI.</strong>{" "}
              OAuth install alone doesn't publish theme blocks — run{" "}
              <code className="text-[11px] font-mono bg-neutral-100 dark:bg-neutral-800 px-1 rounded">cd adapters/shopify && shopify app deploy</code>.
              If the "Cost+ Checkout" block doesn't appear <em>anywhere</em> in
              the theme editor, this is almost always the cause. See{" "}
              <code className="text-[11px] font-mono">adapters/shopify/DEPLOY_THEME_EXTENSION.md</code>.
            </li>
            <li>
              <strong>Place the Cost+ block on the Cart page.</strong> Shopify
              admin → Online Store → Themes → Customize → switch the template
              dropdown to <em>Cart</em> (not Product / Home) → <em>Add section</em>{" "}
              → "Cost+ Checkout" under the Apps list. The block only shows up
              on the Cart template (it's gated by{" "}
              <code className="text-[11px] font-mono">enabled_on.templates = ["cart"]</code>).
            </li>
            <li>
              <strong>Disable "Buy Now" / Dynamic Checkout on product pages.</strong>{" "}
              That button skips the cart entirely and goes straight to Shopify
              checkout. Theme customizer → Product template → Buy buttons →
              uncheck <em>Show dynamic checkout button</em>. Wording varies by
              theme (Dawn, Debut, Impulse, etc.).
            </li>
            <li>
              <strong>Cart drawer checkout.</strong> If your theme uses a
              slideout/drawer cart with its own "Check out" link, disable the
              drawer in theme settings, or point buyers to the full cart page.
              Our extension tries to hide common drawer selectors, but themes
              vary.
            </li>
          </ol>
          <p className="pt-1">
            Replacing Shopify's native checkout globally (including Buy Now
            and drawer) requires Shopify <strong>Plus</strong> + Checkout UI
            Extensions. On Basic/Standard/Advanced, the cart-page TAE model
            above is the complete story.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function StoreRow({
  store,
  onMutated,
}: {
  store: ShopifyStore;
  onMutated: () => void;
}) {
  const installedAt = new Date(store.installed_at).toLocaleString();
  const uninstalledAt = store.uninstalled_at ? new Date(store.uninstalled_at).toLocaleString() : null;

  const [syncing, setSyncing] = useState(false);
  async function disconnect() {
    if (
      !confirm(
        `Disconnect ${store.shop_domain}? This zeros the access token, orphans the bound flow (if any), and flushes any live sessions for this shop.`,
      )
    )
      return;
    try {
      await getApiClient().shopify.disconnectStore(store.shop_domain);
      onMutated();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Disconnect failed.");
    }
  }
  async function resync() {
    if (syncing) return;
    setSyncing(true);
    try {
      await getApiClient().shopify.resyncStore(store.shop_domain);
      // Backend returns 202; the inline progress bar polls
      // /sync-progress and unmounts itself when done.
    } catch (err) {
      alert(err instanceof Error ? err.message : "Sync failed.");
      setSyncing(false);
    }
  }

  return (
    <li className="px-4 py-3 space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={
                "inline-block w-2 h-2 rounded-full " +
                (store.active ? "bg-category-trigger" : "bg-neutral-400")
              }
              aria-hidden
            />
            <span className="text-sm font-medium">{store.shop_domain}</span>
            {!store.active ? (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-900 text-neutral-500">
                uninstalled
              </span>
            ) : null}
          </div>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Installed {installedAt}
            {uninstalledAt ? ` · Uninstalled ${uninstalledAt}` : null}
          </p>
          {store.bound_to ? (
            <p className="text-[11px] text-neutral-500 mt-1">
              Used by{" "}
              <Link
                to={`/flows/${store.bound_to.flow_id}`}
                className="text-neutral-900 dark:text-neutral-100 underline decoration-dotted underline-offset-2 hover:text-category-trigger"
              >
                "{store.bound_to.flow_name}"
              </Link>
              <span className="text-neutral-400"> · {store.bound_to.version_state}</span>
            </p>
          ) : store.active ? (
            <p className="text-[11px] text-neutral-500 mt-1">Not used in any flow</p>
          ) : (
            <p className="text-[11px] text-neutral-500 mt-1">
              Re-install on Shopify to use this store again
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={`https://${store.shop_domain}/admin`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-[11px] font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 inline-flex items-center gap-1"
            title="Open the Shopify admin for this store"
          >
            <ExternalLink className="w-3 h-3" />
            Admin
          </a>
          {store.active && !syncing ? (
            <button
              onClick={resync}
              className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-[11px] font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 inline-flex items-center gap-1"
              title="Re-sync shipping zones, automatic discounts, and products from this Shopify store"
            >
              <RefreshCw className="w-3 h-3" />
              Sync
            </button>
          ) : null}
          {store.active ? (
            <button
              onClick={disconnect}
              className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-[11px] font-medium text-neutral-500 hover:text-category-error hover:border-category-error inline-flex items-center gap-1"
              title="Disconnect this store"
            >
              <Trash2 className="w-3 h-3" />
              Disconnect
            </button>
          ) : null}
        </div>
      </div>
      {syncing ? (
        <RealSyncProgress
          shopDomain={store.shop_domain}
          onComplete={() => setSyncing(false)}
        />
      ) : null}
    </li>
  );
}
